import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Button, Avatar, LinearProgress, Alert, IconButton, Menu, MenuItem
} from '@mui/material';
import {
  People, Description, AccountBalance, Settings, SupportAgent,
  MoreVert, Refresh, CheckCircle, Cancel, Schedule, TrendingUp,
  AccountBalanceWallet, Api, CreditCard
} from '@mui/icons-material';
import { getAdminDashboard, getPendingKyc, approveRejectKyc } from '../../services/adminApi';
import { showLoadingToast, updateToast } from '../../utils/toast';
import { formatDateDDMMYYYY } from '../../utils/dateUtils';

const AdminDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [pendingKyc, setPendingKyc] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedKyc, setSelectedKyc] = useState(null);

  useEffect(() => { loadDashboardData(); }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const statsResponse = await getAdminDashboard();
      setDashboardData(statsResponse.data);
      const kycResponse = await getPendingKyc(0, 5);
      setPendingKyc(kycResponse.data?.content || kycResponse.data || []);
      setError('');
    } catch (error) {
      console.error('Failed to load admin dashboard:', error);
      setDashboardData(null);
      setPendingKyc([]);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleMenuClick = (event, kyc) => { setAnchorEl(event.currentTarget); setSelectedKyc(kyc); };
  const handleMenuClose = () => { setAnchorEl(null); setSelectedKyc(null); };

  const handleKycAction = async (action) => {
    if (!selectedKyc) return;
    const toastId = showLoadingToast(`${action} KYC document...`);
    try {
      await approveRejectKyc(selectedKyc.merchantId || selectedKyc.id, {
        approved: action === 'approve',
        rejectionReason: action === 'reject' ? 'Rejected by admin' : null
      });
      updateToast(toastId, `KYC ${action}d successfully!`, 'success');
      await loadDashboardData();
    } catch (error) {
      updateToast(toastId, `Failed to ${action} KYC`, 'error');
    }
    handleMenuClose();
  };

  // Premium stat card
  const StatCard = ({ title, value, icon: Icon, gradient, iconColor, subtitle }) => (
    <Card sx={{
      height: '100%', borderRadius: 3, border: '1px solid #f1f5f9',
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      transition: 'all 0.25s ease',
      '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 8px 24px rgba(0,0,0,0.1)' },
      overflow: 'hidden', position: 'relative'
    }}>
      <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: gradient }} />
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: 2,
            background: `${iconColor}15`,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <Icon sx={{ fontSize: 20, color: iconColor }} />
          </Box>
        </Box>
        <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em', mb: 0.5, fontSize: { xs: '1.4rem', sm: '1.75rem' } }}>
          {value}
        </Typography>
        <Typography variant="body2" sx={{ color: '#475569', fontWeight: 600, fontSize: '0.8rem' }}>{title}</Typography>
        {subtitle && <Typography variant="caption" sx={{ color: '#94a3b8', mt: 0.5, display: 'block' }}>{subtitle}</Typography>}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress sx={{ borderRadius: 1, '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg, #06b6d4, #3b82f6)' } }} />
        <Typography variant="body2" sx={{ mt: 2, textAlign: 'center', color: '#94a3b8' }}>Loading dashboard...</Typography>
      </Box>
    );
  }

  const stats = [
    { title: 'Pending KYC', value: dashboardData?.pendingKycCount ?? 0, icon: Description, gradient: 'linear-gradient(90deg, #f59e0b, #fbbf24)', iconColor: '#f59e0b', subtitle: 'Awaiting review' },
    { title: 'Pending Recharges', value: dashboardData?.pendingRechargeCount ?? 0, icon: CreditCard, gradient: 'linear-gradient(90deg, #06b6d4, #0ea5e9)', iconColor: '#06b6d4', subtitle: 'Fund requests' },
    { title: 'API Requests', value: dashboardData?.pendingApiRequestCount ?? 0, icon: Api, gradient: 'linear-gradient(90deg, #8b5cf6, #a78bfa)', iconColor: '#8b5cf6', subtitle: 'Pending approval' },
    { title: 'Open Tickets', value: dashboardData?.openTicketCount ?? 0, icon: SupportAgent, gradient: 'linear-gradient(90deg, #ef4444, #f87171)', iconColor: '#ef4444', subtitle: 'Support queue' },
    { title: 'Total Merchants', value: dashboardData?.totalMerchants ?? 0, icon: People, gradient: 'linear-gradient(90deg, #3b82f6, #60a5fa)', iconColor: '#3b82f6', subtitle: 'All registered' },
    { title: 'Active Merchants', value: dashboardData?.activeMerchants ?? 0, icon: People, gradient: 'linear-gradient(90deg, #10b981, #34d399)', iconColor: '#10b981', subtitle: 'KYC approved' },
    { title: 'Total Wallet Balance', value: `₹${(dashboardData?.totalWalletBalance || 0).toLocaleString('en-IN')}`, icon: AccountBalanceWallet, gradient: 'linear-gradient(90deg, #06b6d4, #3b82f6)', iconColor: '#06b6d4', subtitle: 'Across all merchants' },
  ];

  return (
    <Box sx={{ maxWidth: 1400 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em' }}>
            Welcome back 👋
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
            Here's what's happening in your platform today.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Refresh sx={{ fontSize: 16 }} />}
          onClick={loadDashboardData}
          sx={{
            borderRadius: 2, textTransform: 'none', fontSize: 13, fontWeight: 600,
            borderColor: '#e2e8f0', color: '#475569',
            '&:hover': { borderColor: '#06b6d4', color: '#06b6d4', background: 'rgba(6,182,212,0.04)' }
          }}
        >
          Refresh
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

      {/* Stats */}
      <Grid container spacing={2.5} sx={{ mb: 4 }}>
        {stats.map((s, i) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
            <StatCard {...s} />
          </Grid>
        ))}
      </Grid>

      {/* Recent KYC */}
      <Paper sx={{ borderRadius: 3, border: '1px solid #f1f5f9', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
        <Box sx={{ p: 2.5, borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a' }}>Recent KYC Requests</Typography>
            <Typography variant="caption" sx={{ color: '#94a3b8' }}>Pending merchant verifications</Typography>
          </Box>
          {pendingKyc.length > 0 && (
            <Button size="small" href="/admin/kyc" sx={{ borderRadius: 2, textTransform: 'none', fontSize: 12, fontWeight: 600, color: '#06b6d4' }}>
              View All
            </Button>
          )}
        </Box>

        {pendingKyc.length === 0 ? (
          <Box sx={{ p: 5, textAlign: 'center' }}>
            <CheckCircle sx={{ fontSize: 40, color: '#10b981', mb: 1.5 }} />
            <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>No pending KYC requests</Typography>
            <Typography variant="caption" sx={{ color: '#94a3b8' }}>All caught up! ✓</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ '& th': { background: '#f8fafc', fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' } }}>
                  <TableCell>Merchant</TableCell>
                  <TableCell>Document Type</TableCell>
                  <TableCell>Submitted</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingKyc.map((kyc) => (
                  <TableRow key={kyc.id} sx={{ '&:hover': { background: '#f8fafc' } }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ width: 32, height: 32, fontSize: '0.8rem', background: 'linear-gradient(135deg, #06b6d4, #3b82f6)' }}>
                          {kyc.merchantName?.charAt(0) || 'M'}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{kyc.merchantName || `Merchant #${kyc.merchantId}`}</Typography>
                          <Typography variant="caption" sx={{ color: '#94a3b8' }}>#{kyc.merchantId}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={kyc.documentType?.replace(/_/g, ' ') || 'Unknown'} size="small" sx={{ fontSize: '0.7rem', fontWeight: 600, borderRadius: 1 }} variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#475569' }}>{formatDateDDMMYYYY(kyc.createdAt)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip icon={<Schedule sx={{ fontSize: '14px !important' }} />} label="Pending" color="warning" size="small" sx={{ fontSize: '0.7rem', fontWeight: 600 }} />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton onClick={(e) => handleMenuClick(e, kyc)} size="small" sx={{ '&:hover': { background: 'rgba(6,182,212,0.08)' } }}>
                        <MoreVert sx={{ fontSize: 18 }} />
                      </IconButton>
                      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose} PaperProps={{ sx: { borderRadius: 2, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', border: '1px solid #f1f5f9' } }}>
                        <MenuItem onClick={() => handleKycAction('approve')} sx={{ fontSize: 13 }}>
                          <CheckCircle sx={{ mr: 1, color: '#10b981', fontSize: 18 }} /> Approve
                        </MenuItem>
                        <MenuItem onClick={() => handleKycAction('reject')} sx={{ fontSize: 13 }}>
                          <Cancel sx={{ mr: 1, color: '#ef4444', fontSize: 18 }} /> Reject
                        </MenuItem>
                      </Menu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default AdminDashboard;
